import { useMemo } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import { useTranslation } from "react-i18next";

export function getPasswordRules(password: string) {
	return {
		minLength: password.length >= 8,
		hasLowercase: /[a-z]/.test(password),
		hasUppercase: /[A-Z]/.test(password),
		hasNumber: /[0-9]/.test(password),
		hasSpecialChar: /[^a-zA-Z0-9]/.test(password),
	};
}

export type PasswordStrengthLevel = 0 | 1 | 2 | 3 | 4;

export function calculatePasswordStrength(password: string): {
	score: PasswordStrengthLevel;
	rulesMet: number;
} {
	if (!password) {
		return { score: 0, rulesMet: 0 };
	}

	const rules = getPasswordRules(password);
	const rulesMet = Object.values(rules).filter(Boolean).length;
	const allRulesMet = rulesMet === Object.keys(rules).length;

	let score: PasswordStrengthLevel = 1;

	if (rulesMet <= 1) {
		score = 1;
	} else if (rulesMet === 2) {
		score = 2;
	} else if (rulesMet === 3) {
		score = 2;
	} else if (allRulesMet) {
		// All requirement chips green → Good (8–11 chars) or Strong (12+)
		score = password.length >= 12 ? 4 : 3;
	} else {
		// 4 of 5 rules met
		score = 3;
	}

	return { score, rulesMet };
}

/** Browser-style strong password (16 chars, mixed charset). */
export function generateStrongPassword(length = 16): string {
	const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
	const lower = "abcdefghijkmnopqrstuvwxyz";
	const numbers = "23456789";
	const special = "@$!%*?&";
	const all = upper + lower + numbers + special;

	const pick = (chars: string) => {
		const index = crypto.getRandomValues(new Uint32Array(1))[0] % chars.length;
		return chars.charAt(index);
	};

	const required = [pick(upper), pick(lower), pick(numbers), pick(special)];
	const rest = Array.from({ length: Math.max(length, 12) - 4 }, () => pick(all));
	const chars = [...required, ...rest];

	for (let i = chars.length - 1; i > 0; i--) {
		const j = crypto.getRandomValues(new Uint32Array(1))[0] % (i + 1);
		[chars[i], chars[j]] = [chars[j], chars[i]];
	}

	return chars.join("");
}

const METER_SEGMENT_COLORS: Record<PasswordStrengthLevel, string> = {
	0: "bg-gray-200",
	1: "bg-red-500",
	2: "bg-amber-400",
	3: "bg-sky-500",
	4: "bg-emerald-500",
};

interface PasswordStrengthHintsProps {
	password: string;
	onSuggestPassword?: (password: string) => void;
	showSuggestButton?: boolean;
}

const PasswordStrengthHints: React.FC<PasswordStrengthHintsProps> = ({
	password,
	onSuggestPassword,
	showSuggestButton = Boolean(onSuggestPassword),
}) => {
	const { t } = useTranslation();
	const ruleItems = useMemo(
		() =>
			(
				[
					["minLength", "psdaCommon.profile.changePassword.rules.minLength", "At least 8 characters long"],
					["hasLowercase", "psdaCommon.profile.changePassword.rules.hasLowercase", "Contains lowercase"],
					["hasUppercase", "psdaCommon.profile.changePassword.rules.hasUppercase", "Contains uppercase"],
					["hasNumber", "psdaCommon.profile.changePassword.rules.hasNumber", "Contains number"],
					[
						"hasSpecialChar",
						"psdaCommon.profile.changePassword.rules.hasSpecialChar",
						"Contains a special character",
					],
				] as const
			).map(([key, i18nKey, fallback]) => ({
				key: key as keyof ReturnType<typeof getPasswordRules>,
				label: t(i18nKey, fallback),
			})),
		[t],
	);
	const rules = getPasswordRules(password);
	const { score } = calculatePasswordStrength(password);

	const strengthLabels: Record<PasswordStrengthLevel, string> = {
		0: t("psdaCommon.profile.changePassword.strength.weak", "Weak"),
		1: t("psdaCommon.profile.changePassword.strength.weak", "Weak"),
		2: t("psdaCommon.profile.changePassword.strength.fair", "Fair"),
		3: t("psdaCommon.profile.changePassword.strength.good", "Good"),
		4: t("psdaCommon.profile.changePassword.strength.strong", "Strong"),
	};

	const activeColor = METER_SEGMENT_COLORS[score];

	const handleSuggest = () => {
		if (!onSuggestPassword) return;
		onSuggestPassword(generateStrongPassword());
	};

	if (!password && !showSuggestButton) return null;

	return (
		<div className="space-y-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-3 sm:col-span-2">
			{(password || showSuggestButton) && (
				<div className="flex flex-wrap items-center justify-between gap-2">
					<div className="min-w-0 flex-1 space-y-1.5">
						<div className="flex items-center justify-between gap-2">
							<span className="text-xs font-medium text-gray-600">
								{t("psdaCommon.profile.changePassword.strength.label", "Password strength")}
							</span>
							{password ? (
								<span
									className={`text-xs font-semibold ${
										score <= 1 ? "text-red-600" : score === 2 ? "text-amber-600" : score === 3 ? "text-sky-600" : "text-emerald-600"
									}`}>
									{strengthLabels[score]}
								</span>
							) : null}
						</div>
						<div
							className="flex gap-1"
							role="meter"
							aria-valuenow={score}
							aria-valuemin={0}
							aria-valuemax={4}
							aria-label={t("psdaCommon.profile.changePassword.strength.label", "Password strength")}>
							{([1, 2, 3, 4] as const).map(level => (
								<div
									key={level}
									className={`h-1.5 flex-1 rounded-full transition-colors duration-200 ${
										password && score >= level ? activeColor : "bg-gray-200"
									}`}
								/>
							))}
						</div>
					</div>
					{showSuggestButton && onSuggestPassword ? (
						<button
							type="button"
							onClick={handleSuggest}
							className="inline-flex shrink-0 items-center gap-1 rounded-md border border-primary/30 bg-white px-2.5 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/5">
							<AutoFixHighOutlinedIcon sx={{ fontSize: 16 }} />
							{t("psdaCommon.profile.changePassword.strength.suggest", "Suggest strong password")}
						</button>
					) : null}
				</div>
			)}

			{password ? (
				<div className="flex flex-wrap gap-x-4 gap-y-2 justify-start border-t border-gray-200 pt-2">
					{ruleItems.map(({ key, label }) => {
						const ok = rules[key];
						return (
							<div
								key={key}
								className={`flex items-center gap-1 text-[11px] font-medium leading-tight sm:text-xs ${ok ? "text-primary" : "text-gray-500"}`}>
								{ok ? (
									<CheckCircleIcon className="h-3.5 w-3.5 shrink-0 text-primary sm:h-4 sm:w-4" />
								) : (
									<CancelIcon className="h-3.5 w-3.5 shrink-0 text-gray-400 sm:h-4 sm:w-4" />
								)}
								<span>{label}</span>
							</div>
						);
					})}
				</div>
			) : null}
		</div>
	);
};

export default PasswordStrengthHints;
